// Cliente REST da Arena.
import type {
  AdminOverview,
  AdminRoom,
  AuditEntry,
  ImportError,
  JevHealth,
  JoinResult,
  Me,
  QuestionDoc,
  Quiz,
  QuizExample,
  Report,
  RoomSettings,
  SessionCreated,
  SessionInfo,
  SessionReport,
  SessionSummary,
  StageTokenResult,
} from "./types";

export class ApiError extends Error {
  code: string;
  status: number;
  hint?: string;
  errors?: ImportError[]; // 422 de importação/validação
  constructor(
    status: number,
    code: string,
    message: string,
    hint?: string,
    errors?: ImportError[],
  ) {
    super(message);
    this.status = status;
    this.code = code;
    this.hint = hint;
    this.errors = errors;
  }
}

async function req<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    headers: body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    credentials: "same-origin",
  });
  const data = (await res.json().catch(() => null)) as
    | (T & {
        error?: { code: string; message: string; hint?: string };
        errors?: ImportError[];
      })
    | null;
  if (!res.ok) {
    const e = data?.error;
    throw new ApiError(
      res.status,
      e?.code ?? "unknown",
      e?.message ?? "Erro inesperado.",
      e?.hint,
      data?.errors,
    );
  }
  return data as T;
}

const get = <T>(p: string) => req<T>("GET", p);
const post = <T>(p: string, b?: unknown) => req<T>("POST", p, b);
const put = <T>(p: string, b?: unknown) => req<T>("PUT", p, b);
const patch = <T>(p: string, b?: unknown) => req<T>("PATCH", p, b);
const del = <T>(p: string) => req<T>("DELETE", p);

export const api = {
  // auth
  register: (email: string, name: string, password: string) =>
    post<{ user: Me }>("/api/auth/register", { email, name, password }),
  login: (email: string, password: string) =>
    post<{ user: Me }>("/api/auth/login", { email, password }),
  logout: () => post<{ ok: boolean }>("/api/auth/logout"),
  me: () => get<{ user: Me | null }>("/api/auth/me"),
  magicLink: (email: string) => post<{ sent: boolean }>("/api/auth/magic-link", { email }),
  magicConsume: (token: string) => post<{ user: Me }>("/api/auth/magic-link/consume", { token }),
  forgot: (email: string) => post<{ sent: boolean }>("/api/auth/forgot", { email }),
  resetPassword: (token: string, password: string) =>
    post<{ user: Me }>("/api/auth/reset", { token, password }),

  // quizzes
  listQuizzes: () => get<{ quizzes: Quiz[] }>("/api/quizzes"),
  createQuiz: (title: string, description?: string) =>
    post<{ quiz: Quiz }>("/api/quizzes", { title, description }),
  getQuiz: (id: string) => get<{ quiz: Quiz }>(`/api/quizzes/${id}`),
  patchQuiz: (id: string, p: Partial<Pick<Quiz, "title" | "description" | "settings">>) =>
    patch<{ quiz: Quiz }>(
      `/api/quizzes/${id}`,
      p.settings ? { ...p, settings: apiSettings(p.settings) } : p,
    ),
  deleteQuiz: (id: string) => del<{ ok: boolean }>(`/api/quizzes/${id}`),
  duplicateQuiz: (id: string) => post<{ quiz: Quiz }>(`/api/quizzes/${id}/duplicate`),
  putQuestions: (id: string, questions: QuestionDoc[]) =>
    put<{ quiz: Quiz }>(`/api/quizzes/${id}/questions`, { questions }),
  importQuiz: (format: "json" | "csv", content: string, title?: string) =>
    post<{ quiz: Quiz }>("/api/quizzes/import", { format, content, title }),
  examples: () => get<{ examples: QuizExample[] }>("/api/quizzes/examples"),
  copyExample: (key: string) => post<{ quiz: Quiz }>(`/api/quizzes/examples/${key}/copy`),

  // upload de imagem (multipart)
  uploadQuestionImage: async (quizId: string, questionId: string, file: File) => {
    const fd = new FormData();
    fd.append("image", file);
    const res = await fetch(`/api/quizzes/${quizId}/questions/${questionId}/image`, {
      method: "POST",
      body: fd,
      credentials: "same-origin",
    });
    const data = (await res.json().catch(() => null)) as {
      question?: QuestionDoc;
      error?: { code: string; message: string; hint?: string };
    } | null;
    if (!res.ok) {
      const e = data?.error;
      throw new ApiError(
        res.status,
        e?.code ?? "unknown",
        e?.message ?? "Erro no upload.",
        e?.hint,
      );
    }
    return data?.question;
  },

  // sessões
  createSession: (quizId: string, settings?: RoomSettings) =>
    post<SessionCreated>("/api/sessions", { quizId, settings }),
  sessionInfo: (code: string) => get<SessionInfo>(`/api/sessions/${code}`),
  joinSession: (code: string, nickname: string, avatar?: string, team?: number) =>
    post<JoinResult>(`/api/sessions/${code}/join`, { nickname, avatar, team }),
  stageToken: (code: string) => get<StageTokenResult>(`/api/sessions/${code}/stage-token`),
  mySessions: () => get<{ sessions: SessionSummary[] }>("/api/sessions"),
  sessionReport: (code: string) => get<SessionReport>(`/api/sessions/${code}/report`),
  correctAnswer: (code: string, answerId: string, verdict: string) =>
    post<{ report: Report }>(`/api/sessions/${code}/answers/${answerId}/correct`, { verdict }),

  // admin (M6)
  adminRooms: () => get<{ rooms: AdminRoom[] }>("/api/admin/rooms"),
  adminEndRoom: (code: string) => post<{ ended: string }>(`/api/admin/rooms/${code}/end`),
  adminJev: () => get<JevHealth>("/api/admin/jev/health"),
  adminOverview: () => get<AdminOverview>("/api/admin/overview"),
  adminAudit: () => get<{ entries: AuditEntry[] }>("/api/admin/audit"),
};

export function reportCsvUrl(code: string, kind: "players" | "answers"): string {
  return `/api/sessions/${code}/report.csv?kind=${kind}`;
}

export function qrUrl(code: string): string {
  return `/qr/${code.toUpperCase()}.png`;
}

export function exportUrl(id: string, format: "json" | "csv"): string {
  return `/api/quizzes/${id}/export?format=${format}`;
}

// Chaves documentadas de settings (docs/04 §1) — lista explícita para não
// mandar lixo ao backend (M4: nicknameMode/roster/prefix já são aceitos).
function apiSettings(s: RoomSettings): RoomSettings {
  return {
    speedScoring: s.speedScoring,
    streak: s.streak,
    teams: s.teams,
    allowEdit: s.allowEdit,
    showCommonAnswers: s.showCommonAnswers,
    reviewPolicy: s.reviewPolicy,
    reviewWaitSeconds: s.reviewWaitSeconds,
    maxPlayers: s.maxPlayers,
    autoAdvance: s.autoAdvance,
    nicknameMode: s.nicknameMode,
    nicknamePrefix: s.nicknamePrefix,
    classRoster: s.classRoster,
  };
}
